import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ActiveOrderQueue from './components/ActiveOrderQueue';
import DriverMapView from './components/DriverMapView';
import EarningsSummary from './components/EarningsSummary';
import PerformanceMetrics from './components/PerformanceMetrics';
import ReferralSection from './components/ReferralSection';
import OrderHistoryPanel from './components/OrderHistoryPanel';
import NewOrderToast from './components/NewOrderToast';
import BroadcastModal from './components/BroadcastModal';
import { driverService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { NotificationBell } from '../../components/NotificationPanel';
import NotificationPreferencesDrawer from '../../components/NotificationPreferencesDrawer';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';
import ProofOfDeliveryUploader from './components/ProofOfDeliveryUploader';


const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const INCOMING_ORDERS_POOL = [
  { id: 'OTG-9021', type: 'ride', pickup: 'Westlands Roundabout', dropoff: 'JKIA Terminal 1A', fare: 850, distance: '14.2 km', eta: '6 min away', priority: 'high', passengerName: 'Amina K.', passengerRating: 4.7, surgeMultiplier: 1.4 },
  { id: 'OTG-9022', type: 'delivery', pickup: 'Carrefour Sarit Centre', dropoff: 'Lavington Green Apts', fare: 320, distance: '5.8 km', eta: '3 min away', priority: 'normal', passengerName: 'Brian O.', passengerRating: 4.9, surgeMultiplier: null },
  { id: 'OTG-9023', type: 'ride', pickup: 'Karen Shopping Centre', dropoff: 'CBD Kencom Stage', fare: 620, distance: '11.5 km', eta: '4 min away', priority: 'normal', passengerName: 'Cynthia M.', passengerRating: 4.8, surgeMultiplier: null },
  { id: 'OTG-9024', type: 'delivery', pickup: 'Kilimani Naivas', dropoff: 'Hurlingham Flats', fare: 280, distance: '3.2 km', eta: '2 min away', priority: 'high', passengerName: 'David N.', passengerRating: 4.6, surgeMultiplier: 1.2 },
];

const playNewOrderSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const playTone = (freq, startTime, duration, gainVal) => {
      const osc = ctx?.createOscillator();
      const gainNode = ctx?.createGain();
      osc?.connect(gainNode);
      gainNode?.connect(ctx?.destination);
      osc.type = 'sine';
      osc?.frequency?.setValueAtTime(freq, startTime);
      gainNode?.gain?.setValueAtTime(0, startTime);
      gainNode?.gain?.linearRampToValueAtTime(gainVal, startTime + 0.02);
      gainNode?.gain?.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc?.start(startTime);
      osc?.stop(startTime + duration);
    };
    const now = ctx?.currentTime;
    playTone(880, now, 0.25, 0.4);
    playTone(1100, now + 0.18, 0.25, 0.4);
    playTone(1320, now + 0.36, 0.35, 0.5);
    setTimeout(() => ctx?.close(), 1500);
  } catch (e) {
    console.warn('Audio notification failed:', e);
  }
};

const GEOFENCE_RADIUS_M = 200;

const TABS = [
  { id: 'orders', label: 'Orders', icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>) },
  { id: 'map', label: 'Map', icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/></svg>) },
  { id: 'earnings', label: 'Earnings', icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3 3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>) },
  { id: 'history', label: 'History', icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>) },
  { id: 'referral', label: 'Refer', icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>) },
];

const DriverDashboardScreen = () => {
  const navigate = useNavigate();
  const [driverProfile, setDriverProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [navigationActive, setNavigationActive] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [showEmergency, setShowEmergency] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [currentGps, setCurrentGps] = useState(null);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [orderToasts, setOrderToasts] = useState([]);
  const toastIntervalRef = useRef(null);
  const usedOrderIdsRef = useRef(new Set());
  const [broadcastOrder, setBroadcastOrder] = useState(null);
  const broadcastChannelRef = useRef(null);
  const [showArrivalBanner, setShowArrivalBanner] = useState(false);
  const [tripCompleting, setTripCompleting] = useState(false);
  const [tripCompleted, setTripCompleted] = useState(false);
  const [acceptingOrder, setAcceptingOrder] = useState(false);
  const [orderAcceptedToast, setOrderAcceptedToast] = useState({ visible: false, message: '', subMessage: '' });
  const [tripCompletedToast, setTripCompletedToast] = useState(false);
  const watchIdRef = useRef(null);
  const broadcastIntervalRef = useRef(null);
  const lastGpsRef = useRef(null);
  const geofenceTriggeredRef = useRef(false);
  const [showProofUploader, setShowProofUploader] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);

  useEffect(() => {
    const fetchDriverProfile = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const profile = await driverService?.getDriverProfile(user?.id);
          if (profile) {
            setDriverProfile(profile);
            setIsOnline(profile?.status === 'online');
          }
        }
      } catch (err) {
        console.error('Failed to load driver profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDriverProfile();
  }, []);

  useEffect(() => {
    if (!isOnline || !driverProfile?.id) {
      if (broadcastChannelRef?.current) {
        supabase?.removeChannel(broadcastChannelRef?.current);
        broadcastChannelRef.current = null;
      }
      return;
    }
    broadcastChannelRef.current = supabase?.channel(`driver-broadcasts-${driverProfile?.id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'driver_broadcast_requests',
        filter: `driver_id=eq.${driverProfile?.id}`,
      }, async (payload) => {
        const broadcast = payload?.new;
        if (!broadcast || broadcast?.status !== 'pending') return;
        if (activeOrder) return;
        try {
          const { data: orderData, error } = await supabase?.from('orders')?.select('*')?.eq('id', broadcast?.order_id)?.single();
          if (error || !orderData) return;
          setBroadcastOrder({
            id: orderData?.id,
            orderNumber: orderData?.order_number,
            serviceType: orderData?.service_type,
            vehicleType: orderData?.vehicle_type,
            pickupAddress: orderData?.pickup_address,
            dropoffAddress: orderData?.dropoff_address,
            distanceKm: orderData?.distance_km,
            totalAmount: orderData?.total_amount,
            deliveryDetails: orderData?.delivery_details,
            broadcastId: broadcast?.id,
            expiresAt: broadcast?.expires_at,
          });
          playNewOrderSound();
        } catch (err) {
          console.error('Failed to fetch broadcast order:', err);
        }
      })?.subscribe();
    return () => {
      if (broadcastChannelRef?.current) {
        supabase?.removeChannel(broadcastChannelRef?.current);
        broadcastChannelRef.current = null;
      }
    };
  }, [isOnline, driverProfile?.id, activeOrder]);

  const handleBroadcastAccept = async (orderId, driverId) => {
    const { data, error } = await supabase?.rpc('lock_driver_assignment', { p_order_id: orderId, p_driver_id: driverId });
    if (error || !data?.success) throw new Error(data?.error || error?.message || 'Assignment failed');
    const { data: orderData } = await supabase?.from('orders')?.select('*')?.eq('id', orderId)?.single();
    if (orderData) {
      setActiveOrder({ id: orderData?.id, type: orderData?.service_type, pickup: orderData?.pickup_address, dropoff: orderData?.dropoff_address, fare: orderData?.total_amount, distance: `${parseFloat(orderData?.distance_km || 0)?.toFixed(1)} km`, eta: '~5 min away', deliveryDetails: orderData?.delivery_details });
      setActiveTab('map');
      geofenceTriggeredRef.current = false;
      setShowArrivalBanner(false);
      setTripCompleted(false);
      setOrderAcceptedToast({ visible: true, message: orderData?.service_type === 'delivery' ? 'Delivery accepted!' : 'Order accepted!', subMessage: `Heading to ${orderData?.pickup_address}` });
    }
    setBroadcastOrder(null);
  };

  const handleBroadcastReject = async (orderId, driverId) => {
    try { await supabase?.rpc('reject_broadcast', { p_order_id: orderId, p_driver_id: driverId }); } catch (err) { console.warn('Reject broadcast error:', err); }
  };

  const handleAcceptOrder = (order) => {
    setAcceptingOrder(true);
    setTimeout(() => {
      setAcceptingOrder(false);
      setActiveOrder(order);
      setActiveTab('map');
      geofenceTriggeredRef.current = false;
      setShowArrivalBanner(false);
      setTripCompleted(false);
      setOrderAcceptedToast({ visible: true, message: 'Order accepted!', subMessage: `Heading to ${order?.pickup || 'pickup location'}` });
    }, 1200);
  };

  const checkDriverGeofence = useCallback((lat, lng) => {
    if (!activeOrder || geofenceTriggeredRef?.current) return;
    const destLat = activeOrder?.dropoffLat ?? activeOrder?.dropoff_lat;
    const destLng = activeOrder?.dropoffLng ?? activeOrder?.dropoff_lng;
    if (!destLat || !destLng) return;
    const distKm = haversineKm(lat, lng, destLat, destLng);
    if (distKm * 1000 <= GEOFENCE_RADIUS_M) {
      geofenceTriggeredRef.current = true;
      setShowArrivalBanner(true);
      if ('Notification' in window) {
        const send = () => new Notification('You have arrived!', { body: 'Complete the trip.', icon: '/favicon.ico' });
        if (Notification.permission === 'granted') send();
        else if (Notification.permission !== 'denied') Notification.requestPermission()?.then(p => { if (p === 'granted') send(); });
      }
    }
  }, [activeOrder]);

  const startGpsBroadcast = (driverProfileId) => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    setGpsStatus('active');
    watchIdRef.current = navigator.geolocation?.watchPosition(
      (position) => {
        const { latitude, longitude, heading, speed } = position?.coords;
        const gpsData = { lat: latitude, lng: longitude, heading: heading ?? null, speed: speed ?? null };
        lastGpsRef.current = gpsData;
        setCurrentGps(gpsData);
        checkDriverGeofence(latitude, longitude);
      },
      (err) => { console.warn('GPS watch error:', err); setGpsStatus('error'); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
    broadcastIntervalRef.current = setInterval(async () => {
      const gps = lastGpsRef?.current;
      if (!gps || !driverProfileId) return;
      try {
        await supabase?.from('driver_locations')?.insert({ driver_id: driverProfileId, order_id: activeOrder?.id ?? null, latitude: gps?.lat, longitude: gps?.lng, heading: gps?.heading, speed: gps?.speed });
      } catch (err) { console.error('Failed to broadcast location:', err); }
    }, 5000);
  };

  const stopGpsBroadcast = () => {
    if (watchIdRef?.current != null) { navigator.geolocation?.clearWatch(watchIdRef?.current); watchIdRef.current = null; }
    if (broadcastIntervalRef?.current) { clearInterval(broadcastIntervalRef?.current); broadcastIntervalRef.current = null; }
    setGpsStatus('idle');
    setCurrentGps(null);
    lastGpsRef.current = null;
  };

  useEffect(() => { return () => stopGpsBroadcast(); }, []);

  const handleCompleteTrip = async () => {
    if (!activeOrder?.id || !driverProfile?.id) return;
    // For delivery orders, require proof of delivery before completing
    if (activeOrder?.type === 'delivery' && !proofUploaded) {
      setShowProofUploader(true);
      return;
    }
    setTripCompleting(true);
    try {
      const { data, error } = await supabase?.rpc('complete_order_on_arrival', { p_order_id: activeOrder?.id, p_driver_profile_id: driverProfile?.id });
      if (!error && data?.success) {
        setTripCompleted(true);
        setShowArrivalBanner(false);
        setActiveOrder(null);
        setNavigationActive(false);
        setIsOnline(true);
        setDriverProfile(prev => ({ ...prev, status: 'online' }));
        setTripCompletedToast(true);
      }
    } catch (err) { console.error('Complete trip error:', err); }
    finally { setTripCompleting(false); }
  };

  const handleToggleOnline = async () => {
    const newStatus = isOnline ? 'offline' : 'online';
    setIsOnline(!isOnline);
    if (isOnline) { setActiveOrder(null); setNavigationActive(false); stopGpsBroadcast(); setBroadcastOrder(null); }
    else { if (driverProfile?.id) startGpsBroadcast(driverProfile?.id); }
    try {
      if (driverProfile?.id) { await driverService?.updateDriverStatus(driverProfile?.id, newStatus); setDriverProfile(prev => ({ ...prev, status: newStatus })); }
    } catch (err) { console.error('Failed to update status:', err); setIsOnline(isOnline); }
  };

  const displayProfile = driverProfile || { name: 'Driver', vehicleType: 'Boda Boda', vehiclePlate: '—', rating: 5.0, acceptanceRate: 100, completionRate: 100, referralCode: '—', referralCount: 0, referralEarnings: 0, walletBalance: 0 };

  useEffect(() => {
    if (!isOnline) {
      setOrderToasts([]);
      if (toastIntervalRef?.current) { clearInterval(toastIntervalRef?.current); toastIntervalRef.current = null; }
      usedOrderIdsRef.current = new Set();
      return;
    }
    const initialDelay = setTimeout(() => {
      const nextOrder = INCOMING_ORDERS_POOL?.find(o => !usedOrderIdsRef?.current?.has(o?.id));
      if (nextOrder) { usedOrderIdsRef?.current?.add(nextOrder?.id); setOrderToasts(prev => [...prev, nextOrder]); playNewOrderSound(); }
    }, 3000);
    toastIntervalRef.current = setInterval(() => {
      const nextOrder = INCOMING_ORDERS_POOL?.find(o => !usedOrderIdsRef?.current?.has(o?.id));
      if (nextOrder) { usedOrderIdsRef?.current?.add(nextOrder?.id); setOrderToasts(prev => [...prev, nextOrder]); playNewOrderSound(); }
      else { usedOrderIdsRef.current = new Set(); }
    }, 28000);
    return () => {
      clearTimeout(initialDelay);
      if (toastIntervalRef?.current) { clearInterval(toastIntervalRef?.current); toastIntervalRef.current = null; }
    };
  }, [isOnline]);

  const handleToastAccept = (order) => { setOrderToasts(prev => prev?.filter(o => o?.id !== order?.id)); handleAcceptOrder(order); };
  const handleToastDecline = (orderId) => { setOrderToasts(prev => prev?.filter(o => o?.id !== orderId)); };

  return (
    <div className="min-h-screen bg-[#060e1e]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {broadcastOrder && !activeOrder && (
        <BroadcastModal order={broadcastOrder} driverId={driverProfile?.id} onAccept={handleBroadcastAccept} onReject={handleBroadcastReject} onDismiss={() => setBroadcastOrder(null)} />
      )}
      <SpinnerOverlay visible={acceptingOrder} title="Accepting order..." message="Updating your status and routing to pickup" />
      <SpinnerOverlay visible={tripCompleting} title="Completing trip..." message="Recording trip and releasing earnings" />
      <SuccessToast visible={orderAcceptedToast?.visible} message={orderAcceptedToast?.message} subMessage={orderAcceptedToast?.subMessage} onDismiss={() => setOrderAcceptedToast(s => ({ ...s, visible: false }))} duration={3500} position="top" />
      <SuccessToast visible={tripCompletedToast} message="Trip completed!" subMessage="Earnings added to your wallet. You're back online." onDismiss={() => setTripCompletedToast(false)} duration={4000} position="top" />
      {orderToasts?.length > 0 && (
        <div className="fixed top-4 left-0 right-0 z-[60] flex flex-col items-center gap-3 px-4 pointer-events-none">
          {orderToasts?.map((order) => (
            <div key={order?.id} className="w-full max-w-lg pointer-events-auto">
              <NewOrderToast order={order} onAccept={handleToastAccept} onDecline={handleToastDecline} onDismiss={handleToastDecline} />
            </div>
          ))}
        </div>
      )}
      {showArrivalBanner && !tripCompleted && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4">
          <div className="w-full max-w-lg bg-gradient-to-r from-green-600 to-green-500 rounded-2xl px-4 py-3 shadow-2xl border border-green-400/40 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-sm">You have arrived!</p>
              <p className="text-white/80 text-xs truncate">Complete the trip when ready</p>
            </div>
            <button onClick={handleCompleteTrip} disabled={tripCompleting} className="flex-shrink-0 bg-white text-green-700 font-black text-xs px-3 py-2 rounded-xl hover:bg-green-50 transition-all active:scale-95 disabled:opacity-60">
              {tripCompleting ? '...' : 'Complete Trip'}
            </button>
            <button onClick={() => setShowArrivalBanner(false)} className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="w-3.5 h-3.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
      {tripCompleted && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4">
          <div className="w-full max-w-lg bg-[#ffd700] rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#0a1628]/20 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="#0a1628" className="w-4 h-4"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
            </div>
            <p className="text-[#0a1628] font-black text-sm flex-1">Trip completed! You're back online.</p>
            <button onClick={() => setTripCompleted(false)} className="w-7 h-7 rounded-full bg-[#0a1628]/10 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0a1628" strokeWidth="2.5" className="w-3.5 h-3.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
      <div className={`sticky z-30 bg-[#0a1628]/95 backdrop-blur-md border-b border-white/8 transition-all ${showArrivalBanner && !tripCompleted ? 'top-16' : 'top-0'}`}>
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#ffd700] flex items-center justify-center flex-shrink-0">
<span className="text-[#0a1628] font-black text-sm">OG</span>
              </div>
              <div>
                <p className="text-white/40 text-xs">Good morning,</p>
                <p className="text-white font-bold text-sm leading-tight">{displayProfile?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isOnline && (
                <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border transition-all ${gpsStatus === 'active' ? 'bg-blue-500/15 border-blue-500/30' : gpsStatus === 'error' ? 'bg-red-500/15 border-red-500/30' : 'bg-white/5 border-white/10'}`}>
                  {gpsStatus === 'active' && (<><svg viewBox="0 0 24 24" fill="#60a5fa" className="w-3 h-3"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg><span className="text-blue-400 text-xs font-semibold">GPS</span><div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" /></>)}
                  {gpsStatus === 'error' && (<><svg viewBox="0 0 24 24" fill="#f87171" className="w-3 h-3"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg><span className="text-red-400 text-xs font-semibold">No GPS</span></>)}
                </div>
              )}
              <NotificationBell iconColor="rgba(255,255,255,0.7)" badgeColor="bg-red-500" />
              <button onClick={() => setShowNotifPrefs(true)} className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors" title="Notification Preferences">
                <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" className="w-4 h-4">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                </svg>
              </button>
              <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 border transition-all ${isOnline ? 'bg-green-500/15 border-green-500/30' : 'bg-white/5 border-white/10'}`}>
                <div className={`w-2 h-2 rounded-full transition-all ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`} />
                <span className={`text-xs font-semibold ${isOnline ? 'text-green-400' : 'text-white/40'}`}>{isOnline ? 'Online' : 'Offline'}</span>
              </div>
              <button onClick={handleToggleOnline} className={`relative w-14 h-7 rounded-full transition-all duration-300 focus:outline-none ${isOnline ? 'bg-[#ffd700]' : 'bg-white/10'}`}>
                <div className={`absolute top-1 w-5 h-5 rounded-full shadow-md transition-all duration-300 ${isOnline ? 'left-8 bg-[#0a1628]' : 'left-1 bg-white/40'}`} />
              </button>
              <button onClick={() => navigate('/driver-profile-screen')} className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-white/70"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              </button>
            </div>
          </div>
          {activeOrder && (
            <div className="mt-3 bg-[#ffd700]/10 border border-[#ffd700]/20 rounded-xl px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#ffd700] animate-pulse" />
                <span className="text-[#ffd700] text-xs font-semibold">Active: {activeOrder?.type === 'ride' ? 'Ride' : 'Delivery'}</span>
              </div>
              <button onClick={() => { setActiveOrder(null); setNavigationActive(false); }} className="text-white/30 hover:text-white/60 transition-colors text-xs">Complete</button>
            </div>
          )}
          {isOnline && gpsStatus === 'active' && currentGps && (
            <div className="mt-2 bg-blue-500/10 border border-blue-500/20 rounded-xl px-3 py-1.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" fill="#60a5fa" className="w-3.5 h-3.5 flex-shrink-0"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                <span className="text-blue-300 text-xs font-mono">{currentGps?.lat?.toFixed(5)}, {currentGps?.lng?.toFixed(5)}</span>
              </div>
              {currentGps?.speed != null && <span className="text-blue-400 text-xs font-semibold">{Math.round((currentGps?.speed ?? 0) * 3.6)} km/h</span>}
            </div>
          )}
        </div>
        <div className="max-w-lg mx-auto px-4 pb-0">
          <div className="flex">
            {TABS?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className={`flex-1 flex flex-col items-center gap-1 py-2.5 text-xs font-semibold transition-all border-b-2 ${activeTab === tab?.id ? 'text-[#ffd700] border-[#ffd700]' : 'text-white/30 border-transparent hover:text-white/50'}`}>
                <span className={activeTab === tab?.id ? 'text-[#ffd700]' : 'text-white/30'}>{tab?.icon}</span>
                {tab?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-4 pb-24">
        {loading ? (
          <div className="space-y-4">
            <div className="w-full h-52 rounded-2xl bg-[#0f2040] border border-white/8 animate-pulse" />
            {[0, 1, 2]?.map((i) => (
              <div key={i} className="rounded-2xl bg-[#0f2040] border border-white/8 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-white/10 animate-pulse" />
                    <div className="space-y-1">
                      <div className="w-24 h-4 rounded bg-white/10 animate-pulse" />
                      <div className="w-16 h-3 rounded bg-white/10 animate-pulse" />
                    </div>
                  </div>
                  <div className="w-20 h-7 rounded-full bg-white/10 animate-pulse" />
                </div>
                <div className="flex gap-2 pt-1">
                  <div className="flex-1 h-10 rounded-xl bg-white/10 animate-pulse" />
                  <div className="flex-1 h-10 rounded-xl bg-[#ffd700]/20 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {activeTab === 'orders' && (
              <div className="space-y-4">
                <DriverMapView isOnline={isOnline} activeOrder={activeOrder} navigationActive={navigationActive} onToggleNavigation={() => setNavigationActive(prev => !prev)} />
                <div className="flex items-center justify-between">
                  <h2 className="heading-2 text-white">{isOnline ? 'Incoming Requests' : 'Order Queue'}</h2>
                  {isOnline && (
                    <div className="flex items-center gap-1.5 bg-[#ffd700]/10 rounded-full px-2.5 py-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#ffd700] animate-pulse" />
                      <span className="text-[#ffd700] text-xs font-semibold">2 nearby</span>
                    </div>
                  )}
                </div>
                <ActiveOrderQueue isOnline={isOnline} onAcceptOrder={handleAcceptOrder} />
              </div>
            )}
            {activeTab === 'map' && (
              <div className="space-y-4">
                <DriverMapView isOnline={isOnline} activeOrder={activeOrder} navigationActive={navigationActive} onToggleNavigation={() => setNavigationActive(prev => !prev)} />
                <div className="rounded-2xl bg-[#0f2040] border border-white/8 p-4">
                  <h3 className="heading-3 text-white mb-3">Navigation Controls</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setNavigationActive(prev => !prev)} className={`py-4 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2 ${navigationActive ? 'btn-danger' : 'btn-primary'}`}>
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
                      {navigationActive ? 'Stop Nav' : 'Start Nav'}
                    </button>
                    <button className="btn-secondary py-4 flex items-center justify-center gap-2">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
                      Recenter
                    </button>
                  </div>
                </div>
                <div className="rounded-2xl bg-[#0f2040] border border-white/8 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="heading-3 text-white">Vehicle Status</h3>
                    <span className="text-xs text-white/30">{displayProfile?.vehiclePlate}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ label: 'Fuel', value: '72%', color: '#34d399', icon: '⛽' }, { label: 'Battery', value: '88%', color: '#60a5fa', icon: '🔋' }, { label: 'Signal', value: '4G', color: '#ffd700', icon: '📶' }]?.map(item => (
                      <div key={item?.label} className="bg-white/5 rounded-xl p-2.5 text-center">
                        <div className="text-lg mb-1">{item?.icon}</div>
                        <p className="text-xs font-bold" style={{ color: item?.color }}>{item?.value}</p>
                        <p className="text-white/30 text-xs">{item?.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'earnings' && (
              <div className="space-y-4">
                <EarningsSummary driverProfile={displayProfile} walletBalance={displayProfile?.walletBalance} />
                <PerformanceMetrics driverProfile={displayProfile} rating={displayProfile?.rating} acceptanceRate={displayProfile?.acceptanceRate} completionRate={displayProfile?.completionRate} />
              </div>
            )}
            {activeTab === 'referral' && (
              <ReferralSection driverProfile={displayProfile} referralCode={displayProfile?.referralCode} referralCount={displayProfile?.referralCount} referralEarnings={displayProfile?.referralEarnings} />
            )}
            {activeTab === 'history' && <OrderHistoryPanel driverProfileId={driverProfile?.id} />}
          </>
        )}
      </div>
      <div className="fixed bottom-6 right-4 z-30">
        <button onClick={() => setShowEmergency(true)} className="w-14 h-14 rounded-full bg-red-500 shadow-xl flex items-center justify-center hover:bg-red-600 transition-all active:scale-90 border-2 border-red-400">
          <span className="text-white font-black text-xs">SOS</span>
        </button>
      </div>
      {showEmergency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs bg-[#0a1628] rounded-3xl p-6 text-center shadow-2xl border border-red-500/30">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 font-black text-xl">SOS</span>
            </div>
            <h3 className="text-white font-bold text-lg mb-2">Emergency Alert</h3>
            <p className="text-white/50 text-sm mb-6">Contact emergency services or OnTheGo support</p>
            <div className="space-y-2">
              <a href="tel:999" className="btn-danger-full block text-center">Call 999</a>
              <button onClick={() => setShowEmergency(false)} className="btn-secondary-full">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <NotificationPreferencesDrawer isOpen={showNotifPrefs} onClose={() => setShowNotifPrefs(false)} />
      {showProofUploader && activeOrder?.type === 'delivery' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-[#0a1628] rounded-t-3xl p-6 shadow-2xl border-t border-[#ffd700]/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base">Upload Delivery Proof</h3>
              <button
                onClick={() => setShowProofUploader(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="w-4 h-4">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ProofOfDeliveryUploader
              orderId={activeOrder?.id}
              onUploadComplete={(url) => {
                setProofUploaded(true);
                setShowProofUploader(false);
                // Auto-complete trip after proof uploaded
                setTimeout(() => handleCompleteTrip(), 500);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboardScreen;